import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { score, type Letters } from '@/domain/scoring';
import styles from './AnimatedWordExample.module.css';

type CharRole = 'target' | 'segment' | 'plain';

interface Props {
  word: string;
  letters: Letters;
  showScore?: boolean;
  invalidReason?: string;
}

export function AnimatedWordExample({ word, letters, showScore = true, invalidReason }: Props) {
  const result = useMemo(() => (invalidReason ? null : score(word, letters)), [word, letters, invalidReason]);

  const roles: CharRole[] = useMemo(() => {
    const arr: CharRole[] = Array.from(word, () => 'plain' as CharRole);
    if (result) {
      const { l1, l2, l3 } = result.positions;
      arr.forEach((_, i) => {
        if (i === l1 || i === l2 || i === l3) arr[i] = 'target';
        else arr[i] = 'segment';
      });
    }
    return arr;
  }, [word, result]);

  const invalid = !result;

  return (
    <div className={styles.wrap}>
      <div className={invalid ? styles.wordInvalid : styles.word} aria-label={word}>
        {Array.from(word).map((ch, i) => (
          <motion.span
            key={`${ch}-${i}`}
            className={styles[roles[i]]}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: invalid ? 0 : i * 0.05, duration: 0.18, ease: 'easeOut' }}
          >
            {ch}
          </motion.span>
        ))}
      </div>

      {invalid ? (
        <div className={styles.reason}>✕ {invalidReason}</div>
      ) : showScore ? (
        <motion.div
          className={styles.score}
          initial={{ opacity: 0, scale: 0.8, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: word.length * 0.05 + 0.1, duration: 0.25, ease: 'easeOut' }}
          aria-label={`${result!.rawScore} очков`}
        >
          +{result!.rawScore}
        </motion.div>
      ) : null}
    </div>
  );
}
